import type { ReactNode } from "react";
import * as stylex from "@stylexjs/stylex";
import {
  isLiveRequest,
  isLiveSubmission,
  type OnboardingProgress,
} from "../onboarding-progress";
import { shortId } from "../onboarding-summary";
import type { StepKey } from "../onboarding-steps";
import { styles } from "../onboarding.styles";

export function activeLineFor({
  key,
  progress,
  pollBroken,
  agentLabel,
}: Readonly<{
  key: StepKey;
  progress: OnboardingProgress;
  pollBroken: boolean;
  agentLabel: string;
}>): ReactNode {
  switch (key) {
    case "signIn":
    case "player":
      return null;
    case "handOff":
      return null;
    case "build":
      if (pollBroken)
        return `We can’t check on ${agentLabel === "Your agent" ? "your agent" : agentLabel} right now.`;

      return `${agentLabel} is working${progress.player ? ` on player ${progress.player.name}` : ""}. This takes a few minutes.`;
    case "request":
      return "Paste this into the same agent window (closed it? start it again in the same folder and say “continue from Step 5”):";
    case "submit":
      return "Paste this into the same agent window (closed it? start it again in the same folder and say “continue from Step 6”):";
  }
}

export function failureLineFor({
  key,
  progress,
}: Readonly<{
  key: StepKey;
  progress: OnboardingProgress;
}>): ReactNode {
  const request = progress.experienceRequest;

  if (key === "request" && request && !isLiveRequest(progress))
    return (
      <>
        <span {...stylex.props(styles.machine)}>{shortId(request.id)}</span>{" "}
        {request.status === "cancelled"
          ? "was cancelled. Ask again when you are ready."
          : "didn’t run. Ask your agent to try again."}
      </>
    );

  const submission = progress.leagueSubmission;

  if (key === "submit" && submission && !isLiveSubmission(progress))
    return (
      <>
        <span {...stylex.props(styles.machine)}>{shortId(submission.id)}</span>{" "}
        {submission.status === "withdrawn"
          ? "was withdrawn. Ask again when you are ready."
          : "was rejected by the league. Ask your agent to submit it again."}
      </>
    );

  return null;
}
